import { AfterViewInit, Component, OnInit, ViewChild, inject } from '@angular/core';
import { combineEither, LoaderSpinnerComponent, StoreLoader } from '@aston/foundation';
import { Store } from '@ngrx/store';
import { SubsidiariesStoreActions, SubsidiariesStoreSelectors } from 'apps/console/src/app/root-store/subsidiaries-store';
import * as RoutesDefinitions from 'apps/console/src/app/routes-definitions';
import { ActivatedRoute, Router } from '@angular/router';
import { delay, filter, map, switchMap, take } from 'rxjs/operators';
import { AppStoreActions, AppStoreSelectors } from 'apps/console/src/app/root-store/app-store';
import { DomSanitizer } from '@angular/platform-browser';
import { combineLatest, of, Subject } from 'rxjs';
import { FederationsStoreActions, FederationsStoreSelectors } from 'apps/console/src/app/root-store/federations-store';
import { NgbAlertModule, NgbNav, NgbNavModule } from '@ng-bootstrap/ng-bootstrap';
import { NewAccountStoreSelectors } from 'apps/console/src/app/root-store/new-account-store';
import { AsyncPipe } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';

import { IAccountInformationModel } from '../../../user-module/models';
import { SubsidiariesListComponent } from '../../components/subsidiaries-list/subsidiaries-list.component';
import { FederationsListComponent } from '../../../federations-module/components/federations-list/federations-list.component';

@Component({
	selector: 'subsidiaries-page',
	templateUrl: './subsidiaries-page.component.html',
	imports: [AsyncPipe, TranslateModule, NgbNavModule, NgbAlertModule, LoaderSpinnerComponent, SubsidiariesListComponent, FederationsListComponent]
})
export class SubsidiariesPageComponent implements OnInit, AfterViewInit {
	private store = inject(Store);
	private route = inject(ActivatedRoute);
	private router = inject(Router);
	sanitizer = inject(DomSanitizer);

	@ViewChild(NgbNav) tabset;

	path = window.location.href;
	canCreateFederation$ = this.store.select(SubsidiariesStoreSelectors.selectCanCreateFederation);

	hasPendingSubsidiaries$ = this.store.select(SubsidiariesStoreSelectors.selectHasPendingSubsidiaries);
	subsidiaries$ = this.store.select(SubsidiariesStoreSelectors.selectSubsidiaries);

	pendingFederations$ = this.store.select(FederationsStoreSelectors.selectHasPendingFederations).pipe(map(some => ({ some, none: !some })));
	federations$ = this.store.select(FederationsStoreSelectors.selectFederations);

	moBaseUrl$ = this.store.select(AppStoreSelectors.selectConfig).pipe(map(c => c.tenantFrontUrl));
	moUrl$ = this.moBaseUrl$.pipe(
		map(u => this.sanitizer.bypassSecurityTrustResourceUrl(`${u}/authentication/conviction-lack`)));

	loading$ = this.store.select(SubsidiariesStoreSelectors.selectSubsidiariesIsLoading);
	loader = new StoreLoader(
		combineEither(this.loading$, this.store.select(FederationsStoreSelectors.selectFederationsIsLoading)),
		this.store.select(SubsidiariesStoreSelectors.selectSubsidiariesError)
	);

	user$ = this.store.select(AppStoreSelectors.selectUser);

	loaderUser = new StoreLoader(
		this.store.select(AppStoreSelectors.selectUserIsLoading),
		this.store.select(AppStoreSelectors.selectUserError)
	);

	activeTab$ = new Subject<string>();

	showTopBtns$ = combineLatest([this.activeTab$, this.subsidiaries$, this.federations$]).pipe(
		map(([tab, ...lists]) => lists[tab === 'federations' ? 1 : 0 ].length > 20)
	);

	ngOnInit() {
		this.store.dispatch(SubsidiariesStoreActions.LoadSubsidiariesRequest());
		this.store.dispatch(FederationsStoreActions.LoadFederationsRequest());
		this.store.dispatch(AppStoreActions.LoadUserRequest());

		of(null).pipe(
			delay(500),
			switchMap(_ => this.loading$.pipe(filter(loading => !loading), take(1))),
			switchMap(_ =>
				combineLatest([
					this.subsidiaries$.pipe(take(1)),
					this.federations$.pipe(take(1)),
					this.store.select(NewAccountStoreSelectors.selectHasCreationBeenCanceled).pipe(take(1)),
					this.store.select(AppStoreSelectors.selectUser).pipe(filter<IAccountInformationModel>(Boolean), map(u => u.hasFederationOrSubsidiary),take(1)),
				]))
		).subscribe(([s, f, creationCancelled, hasFederationOrSubsidiary]) => {
			if (s.length === 1 && document.body.clientWidth < 1000) this.redirectToTenant(s[0].subsidiaryId);

			if (creationCancelled || hasFederationOrSubsidiary) return;
			if (!s.length && !f.length) this.createNewCompany();
			else if (!s.length) this.redirectToFederations();
		})
	}

	ngAfterViewInit(): void {
		const tabValue = this.route.snapshot.data?.layoutName?.match(/federations/) ? 'federations' : 'subsidiaries';

		this.tabset?.select(tabValue);
		this.activeTab$.next(tabValue);
	}

	onTabChange(event) {
		this.router.navigateByUrl(event.nextId.match(/federations/) ?
			RoutesDefinitions.getFederationsFullPath() :
			RoutesDefinitions.getSubsidiariesFullPath())

		this.activeTab$.next(this.tabset.activeId);
	}

	redirectToFederations() {
		this.router.navigate([RoutesDefinitions.getFederationsFullPath()]);
	}

	createNewCompany() {
		this.router.navigate([RoutesDefinitions.getNewAccountCompanyFullPath()]);
	}

	editMyInformations() {
		this.store.dispatch(AppStoreActions.NavigateToGate({path: '/Account/edit?ReturnUrl=' + encodeURIComponent(this.path)}))
	}

	redirectToTenant(subsidiaryId: number) {
		this.store.dispatch(SubsidiariesStoreActions.RedirectToTenant({subsidiaryId}))
	}

	createNewFederation() {
		this.store.dispatch(AppStoreActions.OpenConfirmationModal(
			{ textsKey: 'Federation.Confirmations.PreCreate.' },
			AppStoreActions.Navigate({ to: [RoutesDefinitions.getFederationCreateFullPath()] })
		))
	}
}
